import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  BookOpen,
  Check,
  ChevronDown,
  Download,
  FileText,
  Heart,
  Layers,
  Mail,
  Mic,
  Minus,
  QrCode,
  RefreshCw,
  Shield,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PublicSiteFooter } from '../components/layout/PublicSiteFooter';
import { TrustProofGrid } from '../components/trust/TrustProofGrid';
import { useAuth } from '../contexts/AuthContext';
import { trackProductEvent } from '../utils/productEvents';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { cn } from '../lib/utils';
import axios from 'axios';
import { DEV_BACKEND_BASE_URL } from '../config/devBackendUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || DEV_BACKEND_BASE_URL;
const API = `${BACKEND_URL}/api`;
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  FAMILY_ARCHIVE_MEMORIAL_CREDIT_BADGE,
  FAMILY_ARCHIVE_MEMORIAL_CREDIT_DETAIL,
  FAMILY_ARCHIVE_MEMORIAL_CREDIT_NOTE,
  MEMORIAL_PAGE_FAMILY_ARCHIVE_CREDIT_DETAIL,
  MEMORIAL_PAGE_FAMILY_ARCHIVE_CREDIT_LINE,
  MEMORIAL_PAGE_PRICING_LINE,
} from '../content/familyArchiveMemorialCredit';

const SUPPORT_EMAIL = 'support@mylegacybridge.com';

const heritageCardHover =
  'transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-[#d4c4a8]/70 hover:shadow-[0_20px_48px_-28px_rgba(28,25,23,0.16)]';

const heritageOutlineBtn =
  'border-[hsl(43_38%_52%_/_0.5)] bg-[hsl(43_32%_98%)] !text-[hsl(24_14%_16%)] shadow-sm ' +
  'hover:!bg-[hsl(38_18%_92%)] hover:!text-[hsl(24_14%_16%)] hover:!border-[hsl(43_38%_52%_/_0.7)] ' +
  'group-hover:!border-[hsl(43_38%_52%_/_0.55)] group-hover:!shadow-[0_8px_24px_-14px_hsl(28_14%_18%_/_0.16)] ' +
  'focus-visible:!bg-[hsl(43_32%_98%)] focus-visible:!text-[hsl(24_14%_16%)] focus-visible:!border-[hsl(43_48%_58%)] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_48%_58%_/_0.4)] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(42_28%_96%)] ' +
  'active:!bg-[hsl(38_18%_88%)] active:!text-[hsl(24_14%_16%)]';

const heritagePrimaryBtn =
  '!border-[hsl(152_36%_20%)] !bg-[hsl(152_36%_20%)] !text-white shadow-[0_8px_24px_-10px_hsl(152_36%_20%_/_0.55)] ' +
  'hover:!border-[hsl(152_32%_24%)] hover:!bg-[hsl(152_32%_24%)] hover:!text-white ' +
  'group-hover:!shadow-[0_12px_32px_-10px_hsl(152_36%_20%_/_0.6)] ' +
  'focus-visible:!border-[hsl(152_36%_20%)] focus-visible:!bg-[hsl(152_36%_20%)] focus-visible:!text-white ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_48%_58%_/_0.45)] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(42_28%_96%)] ' +
  'active:!border-[hsl(152_36%_17%)] active:!bg-[hsl(152_36%_17%)] active:!text-white ' +
  '[&_svg]:!text-white';

const heritageWaitlistBtn =
  'border-[hsl(38_16%_78%)] bg-[hsl(43_32%_99%)] !text-[hsl(32_10%_32%)] shadow-sm ' +
  'hover:!bg-[hsl(42_28%_97%)] hover:!text-[hsl(24_14%_16%)] hover:!border-[hsl(43_38%_52%_/_0.45)] ' +
  'focus-visible:!bg-[hsl(43_32%_99%)] focus-visible:!text-[hsl(24_14%_16%)] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_48%_58%_/_0.35)] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(42_28%_96%)] ' +
  '[&_svg]:!text-[hsl(38_36%_42%)]';

const planCardInteractive =
  'transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[hsl(43_48%_58%_/_0.45)] hover:shadow-[0_20px_48px_-24px_hsl(28_14%_18%_/_0.2)]';

const planCtaPill =
  'h-12 w-full rounded-full px-6 text-sm font-semibold tracking-wide transition-all duration-300';

const planCtaFooterMinH = 'min-h-[5.75rem]';

const planStatusPillSmall =
  'inline-flex items-center rounded-full border border-[#e8e2d8] bg-[#f5f2eb]/90 px-3 py-1 text-[0.68rem] font-medium uppercase tracking-[0.08em] text-[#6c6258]';

const modelStrip = [
  { icon: Layers, label: 'Start free — People → Memories → Legacy Book' },
  { icon: BookOpen, label: 'Legacy Book when you are ready' },
  { icon: RefreshCw, label: 'Family Archive — loyalty memorial credit after 12 months' },
  { icon: Heart, label: 'Memorial pages — lifetime, not monthly' },
];

const modelSteps = [
  { icon: Layers, title: 'Start Your Legacy', body: 'Add loved ones, memories and preview your book.' },
  { icon: BookOpen, title: 'Create Legacy Book', body: 'One-time founding offer for the finished keepsake PDF.' },
  { icon: RefreshCw, title: 'Keep growing', body: 'Family Archive subscription for ongoing preservation (coming soon).' },
  { icon: Heart, title: 'Memorial & support', body: 'Lifetime memorial pages and done-for-you help when you need it.' },
];

/** Stage 4B.1/4B.5 — journey-led offers. Legacy Book CTA uses checkout when configured. */
const primaryOffers = [
  {
    id: 'start-legacy',
    anchorId: null,
    name: 'Start Your Legacy',
    priceMain: 'Free',
    priceCompare: null,
    priceSuffix: null,
    badge: 'Start here',
    note: 'Start with a small family archive and preview your Legacy Book.',
    cta: 'Start free',
    ctaMode: 'register',
    featured: false,
    bullets: [
      'Add a few loved ones',
      'Save your first memories',
      'Add photos and stories',
      'Preview your Legacy Book',
      'Upgrade when you are ready for the finished keepsake',
    ],
    details: [
      'Private-by-default archive',
      'No card required',
      'Gentle limits may apply as we grow — your data stays yours',
    ],
  },
  {
    id: 'legacy-book',
    anchorId: 'legacy-book',
    name: 'Legacy Book',
    priceMain: '£49',
    priceCompare: null,
    priceSuffix: 'founding offer · one-time per archive',
    badge: 'Finished keepsake',
    note: 'Create and download a finished family Legacy Book PDF.',
    cta: 'Get Legacy Book',
    ctaMode: 'checkout-legacy-book',
    featured: true,
    bullets: [
      'Full Legacy Book PDF export',
      'More room for loved ones, memories and photos',
      'Include or exclude people from your book',
      'QR link back to the online preview',
      'Re-export as your book grows',
    ],
    details: [
      'Founding members who purchased before this page keep their access',
      'Existing archive checkout remains available for eligible upgrades — see below if you already have a founding plan',
    ],
    ctaHelper: null,
  },
  {
    id: 'family-archive',
    anchorId: 'family-archive',
    name: 'Family Archive',
    priceMain: '£7.99',
    priceCompare: null,
    priceSuffix: '/mo or £79/yr · subscription coming soon',
    badge: 'Ongoing preservation',
    loyaltyBadge: FAMILY_ARCHIVE_MEMORIAL_CREDIT_BADGE,
    note: FAMILY_ARCHIVE_MEMORIAL_CREDIT_NOTE,
    subnote: 'Keep building memories over time — subscription billing opens later.',
    cta: 'Join Family Archive waitlist',
    ctaMode: 'waitlist-family',
    featured: false,
    bullets: [
      'Ongoing family archive',
      'More memories, photos and voice memories',
      'Story polish and voice memory support',
      'Family contributors when available',
      'Legacy Book updates over time',
      '1 lifetime Memorial Page credit after 12 paid months',
    ],
    details: [
      'Ongoing family archive',
      'More memories, photos and voice memories',
      'Story polish / voice memory support',
      'Family contributors when available',
      'Legacy Book updates over time',
      '1 lifetime Memorial Page credit after 12 paid months',
      FAMILY_ARCHIVE_MEMORIAL_CREDIT_DETAIL,
    ],
    detailsLabel: "What's included?",
    ctaHelper: 'Coming soon. Join the waitlist and we’ll email you at launch.',
  },
];

const serviceOffers = [
  {
    id: 'memorial-page',
    anchorId: 'memorial-page',
    name: 'Memorial Page',
    priceMain: '£79',
    priceCompare: '£99',
    priceSuffix: 'founder’s offer · £99 standard lifetime later',
    loyaltyBadge: 'Family Archive loyalty credit',
    note: MEMORIAL_PAGE_PRICING_LINE,
    subnote: MEMORIAL_PAGE_FAMILY_ARCHIVE_CREDIT_LINE,
    bullets: [
      'Lifetime memorial page',
      'Created from an existing person profile',
      'Review before publishing',
      'Photos, story, memories and tribute details',
      'QR-ready destination later',
    ],
    details: [
      'Lifetime memorial page',
      'Created from an existing person profile',
      'Review before publishing',
      'Photos, story, memories and tribute details',
      'QR-ready destination later',
      MEMORIAL_PAGE_PRICING_LINE,
      'Included as 1 lifetime credit after 12 paid months on Family Archive',
      MEMORIAL_PAGE_FAMILY_ARCHIVE_CREDIT_DETAIL,
    ],
    detailsLabel: "What's included?",
    cta: 'Request memorial page',
    ctaMode: 'contact-memorial',
  },
  {
    id: 'done-for-you',
    anchorId: 'done-for-you',
    name: 'Done-for-you Legacy Book',
    priceMain: 'From £299',
    priceSuffix: 'manual service · by enquiry',
    note: 'We help build the book for families who need support.',
    bullets: [
      'We help organise photos and stories',
      'We polish the life story',
      'We prepare the Legacy Book',
      'Optional memorial page setup',
      'Ideal for busy families or bereavement support',
    ],
    cta: 'Enquire',
    ctaMode: 'contact-service',
  },
];

const pricingFaq = [
  {
    q: 'Can I start for free?',
    a: 'Yes. Start Your Legacy is free — add loved ones, save memories and preview your Legacy Book. Upgrade when you want the finished keepsake PDF.',
  },
  {
    q: 'Is the Legacy Book a subscription?',
    a: 'No. Legacy Book is a one-time founding offer per archive for the finished PDF. Family Archive (ongoing tools) will be a separate subscription when it launches.',
  },
  {
    q: 'Can I keep adding memories later?',
    a: 'Yes. Your archive keeps growing. Re-export your Legacy Book as you add more. Family Archive subscription will add ongoing voice, AI and sharing tools when available.',
  },
  {
    q: 'What is the Family Archive Memorial Page credit?',
    a: 'Family Archive members receive one lifetime Memorial Page credit after 12 paid months. It applies to one person profile in your archive. Extra memorial pages remain available separately. Publishing still requires your approval.',
  },
  {
    q: 'Are memorial pages monthly?',
    a: 'No. Memorial Page is a lifetime one-time add-on (£79 founder’s offer, £99 standard later), not a monthly memorial subscription. Family Archive includes one lifetime credit after 12 paid months.',
  },
  {
    q: 'What happens to existing founding members?',
    a: 'If you already purchased a founding Family or Legacy Archive upgrade, your access is unchanged. This page describes our simplified product direction — billing SKUs will align in a later stage.',
  },
  {
    q: 'Can you help build the book for me?',
    a: 'Yes. Done-for-you Legacy Book is a manual service from £299. Contact us and we will discuss your family’s needs.',
  },
];

const comparisonGroups = [
  {
    id: 'journey',
    title: 'Core journey',
    subtitle: 'People → Memories → Legacy Book',
    icon: BookOpen,
    rows: [
      { icon: UserPlus, feature: 'Loved ones & stories', free: 'Start small', legacyBook: 'Expanded room', familyArchive: 'Ongoing growth', memorial: '—' },
      { icon: BookOpen, feature: 'Legacy Book preview', free: 'Included', legacyBook: 'Included', familyArchive: 'Included', memorial: '—' },
      { icon: Download, feature: 'Legacy Book PDF export', free: 'Upgrade for full export', legacyBook: 'Full export', familyArchive: 'Included', memorial: '—' },
      { icon: QrCode, feature: 'QR link in keepsake', free: '—', legacyBook: 'Included', familyArchive: 'Included', memorial: 'Memorial QR' },
    ],
  },
  {
    id: 'ongoing',
    title: 'Ongoing tools',
    subtitle: 'Voice, AI polish and family sharing (Family Archive — coming soon)',
    icon: Mic,
    rows: [
      { icon: Mic, feature: 'Voice memories', free: 'Try in app', legacyBook: 'More room', familyArchive: 'Included (planned)', memorial: '—' },
      { icon: Sparkles, feature: 'AI transcription & polish', free: 'Try in app', legacyBook: '—', familyArchive: 'Allowance (planned)', memorial: '—' },
      { icon: UserPlus, feature: 'Family contributors', free: 'Invite when ready', legacyBook: '—', familyArchive: 'Included (planned)', memorial: 'Tributes' },
      { icon: Bell, feature: 'Living archive reminders', free: '—', legacyBook: '—', familyArchive: 'Planned', memorial: '—' },
    ],
  },
  {
    id: 'memorial-service',
    title: 'Memorial & done-for-you',
    subtitle: 'Lifetime add-ons and human support',
    icon: Heart,
    rows: [
      { icon: Heart, feature: 'Memorial page', free: 'Draft in app', legacyBook: '—', familyArchive: '1 credit after 12 mo', memorial: 'Lifetime add-on' },
      { icon: FileText, feature: 'Done-for-you Legacy Book', free: '—', legacyBook: '—', familyArchive: '—', memorial: 'From £299' },
    ],
  },
];

const importantNotes = [
  {
    id: 'honest-limits',
    title: 'Gentle limits',
    body: 'We are aligning limits with this pricing model in a later stage. Today you can explore freely; upgrade prompts will stay calm and never lock you out of your data.',
  },
  {
    id: 'checkout',
    title: 'Checkout & founding offers',
    body: 'Legacy Book checkout opens when STRIPE_PRICE_LEGACY_BOOK is configured on the server. Contact us to reserve founding access until then. Existing /billing/checkout paths remain for eligible founding Family/Legacy Archive upgrades.',
  },
  {
    id: 'memorial-credit',
    title: 'Family Archive memorial credit',
    body: 'After 12 paid months on Family Archive, members receive one lifetime Memorial Page credit for one person profile. Once used, the memorial page remains lifetime even if you later cancel. This is a loyalty benefit — not automatic publishing.',
  },
  {
    id: 'coming-soon',
    title: 'Coming soon',
    body: 'Legacy Book checkout is available when Stripe is configured. Family Archive subscription, Memorial Page checkout and transcription top-ups are not live yet. Join a waitlist or contact us — we will not charge until billing is ready.',
  },
];

function mailtoEnquiry(subject) {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

function isOfferCurrent(offerId, userPlanType, billingState) {
  if (offerId === 'start-legacy') {
    return !userPlanType || userPlanType === 'free';
  }
  if (offerId === 'legacy-book') {
    if (userPlanType === 'family' || userPlanType === 'premium') return true;
    return Boolean(billingState?.has_legacy_book_purchase);
  }
  return false;
}

function resolveOfferCta(offer, userPlanType, legacyBookCheckoutEnabled, isLoggedIn) {
  switch (offer.ctaMode) {
    case 'register':
      return { href: '/register', external: false };
    case 'waitlist-family':
      return { href: mailtoEnquiry('Family Archive waitlist'), external: true };
    case 'checkout-legacy-book':
      if (legacyBookCheckoutEnabled) {
        return {
          href: isLoggedIn ? '/billing/checkout?plan=legacy_book' : '/register?plan=legacy_book',
          external: false,
        };
      }
      return {
        href: mailtoEnquiry('Legacy Book founding offer — reserve access'),
        external: true,
        fallback: true,
      };
    case 'contact-legacy-book':
      return { href: mailtoEnquiry('Legacy Book founding offer — reserve access'), external: true };
    default:
      return { href: '/register', external: false };
  }
}

function resolveServiceCta(offer) {
  if (offer.ctaMode === 'contact-memorial') {
    return mailtoEnquiry('Memorial Page enquiry');
  }
  if (offer.ctaMode === 'contact-service') {
    return mailtoEnquiry('Done-for-you Legacy Book enquiry');
  }
  return mailtoEnquiry('MyLegacyBridge enquiry');
}

function isEmptyCell(value) {
  return value === '—';
}

function ComparisonCell({ value }) {
  if (isEmptyCell(value)) {
    return <Minus className="h-4 w-4 text-[#c4b8ae]" strokeWidth={1.5} aria-label="Not included" />;
  }
  const isPlanned = /planned|coming soon|upgrade|try in app|draft/i.test(value);
  return <span className={cn(isPlanned && 'text-[#6c6258]')}>{value}</span>;
}

function ComparisonGroupAccordion({ groups }) {
  return (
    <Accordion type="multiple" defaultValue={[]} className="mt-8 space-y-3">
      {groups.map((group) => {
        const GroupIcon = group.icon || BookOpen;
        return (
          <AccordionItem key={group.id} value={group.id} className="border-0">
            <AccordionTrigger
              className={cn(
                'group mb-0 flex w-full cursor-pointer items-center gap-3 rounded-xl border border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)] px-4 py-4 text-left shadow-[0_4px_20px_-12px_hsl(28_14%_18%_/_0.12)] transition-all duration-200',
                'hover:border-[hsl(43_48%_58%_/_0.45)] hover:bg-[hsl(42_28%_97%)]',
                'data-[state=open]:border-[hsl(43_48%_58%_/_0.5)] data-[state=open]:bg-[hsl(42_28%_97%)]',
                'hover:no-underline [&>svg:last-child]:hidden'
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#e8e2d8] bg-[#faf8f5] text-[#8b7355]">
                <GroupIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
              </span>
              <span className="min-w-0 flex-1 pr-2">
                <span className="block font-serif text-base text-[#1c1917]">{group.title}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-[#6c6258]">{group.subtitle}</span>
              </span>
              <ChevronDown
                className="h-5 w-5 shrink-0 text-[#8b7355] transition-transform duration-200 group-data-[state=open]:rotate-180"
                aria-hidden
              />
            </AccordionTrigger>
            <AccordionContent className="overflow-hidden pb-1 pt-3">
              <ComparisonTable rows={group.rows} />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function ComparisonTable({ rows }) {
  const columns = [
    { key: 'free', label: 'Start free' },
    { key: 'legacyBook', label: 'Legacy Book' },
    { key: 'familyArchive', label: 'Family Archive' },
    { key: 'memorial', label: 'Memorial' },
  ];

  return (
    <div className="overflow-x-auto rounded-xl border border-[#e8e2d8]/80 bg-[#faf8f5]/50">
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="border-b border-[#e8e2d8] bg-[#faf8f5]/90">
            <th
              scope="col"
              className="sticky left-0 z-10 bg-[#faf8f5] px-4 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#8b7355]"
            >
              Feature
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className="px-3 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-[#68645e]"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const Icon = row.icon;
            return (
              <tr key={row.feature} className={cn(index % 2 === 1 && 'bg-[#faf8f5]/50')}>
                <td className="sticky left-0 z-10 border-b border-[#e8e2d8]/80 bg-inherit px-4 py-3">
                  <span className="flex items-center gap-2 text-sm font-medium text-[#3d3833]">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#e8e2d8]/80 bg-[#fffcf7] text-[#8b7355]">
                      <Icon className="h-3 w-3" strokeWidth={1.5} aria-hidden />
                    </span>
                    {row.feature}
                  </span>
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="border-b border-[#e8e2d8]/80 px-3 py-3 text-sm">
                    <ComparisonCell value={row[col.key]} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PrimaryOfferCard({ offer, userPlanType, billingState, legacyBookCheckoutEnabled, isLoggedIn }) {
  const [showDetails, setShowDetails] = useState(false);
  const isCurrent = isOfferCurrent(offer.id, userPlanType, billingState);
  const cta = resolveOfferCta(offer, userPlanType, legacyBookCheckoutEnabled, isLoggedIn);
  const isFeaturedPrimary = offer.featured && (offer.ctaMode === 'checkout-legacy-book' || offer.ctaMode === 'contact-legacy-book');
  const isWaitlist = offer.ctaMode === 'waitlist-family';
  const showMailIcon = cta.external;
  const ctaLabel = cta.fallback ? 'Reserve founding offer' : offer.cta;
  const ctaHelper =
    offer.ctaHelper
    || (offer.id === 'legacy-book' && cta.fallback
      ? 'Online checkout is being updated. Contact us to access the founding offer.'
      : null);

  const primaryCtaClass = cn(
    planCtaPill,
    isFeaturedPrimary && heritagePrimaryBtn,
    offer.id === 'start-legacy' && heritageOutlineBtn,
    isWaitlist && heritageWaitlistBtn
  );

  return (
    <article
      id={offer.anchorId || undefined}
      className={cn(
        'group relative flex h-full min-h-0 scroll-mt-24 flex-col rounded-2xl border p-6 shadow-sm sm:p-7',
        planCardInteractive,
        offer.featured
          ? 'border-[hsl(43_48%_58%_/_0.55)] bg-gradient-to-b from-[hsl(43_32%_99%)] via-[hsl(42_28%_97%)] to-[hsl(38_18%_92%)] ring-2 ring-[hsl(43_48%_58%_/_0.28)]'
          : 'border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)]'
      )}
    >
      {offer.badge ? (
        <span
          className={cn(
            'mb-3 inline-flex w-fit rounded-full border px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em]',
            offer.featured
              ? 'border-[#c9a961]/45 bg-[#c9a961]/12 text-[#5c4a32]'
              : 'border-[#e8e2d8] bg-[#faf8f5]/80 text-[#8b7355]'
          )}
        >
          {offer.badge}
        </span>
      ) : null}

      {offer.loyaltyBadge ? (
        <span className="mb-3 inline-flex w-fit rounded-full border border-[#e8e2d8]/90 bg-[#fffcf7] px-2.5 py-0.5 text-[0.62rem] font-medium tracking-[0.04em] text-[#6c6258]">
          {offer.loyaltyBadge}
        </span>
      ) : null}

      <h2 className="font-serif text-2xl font-normal tracking-tight text-[#1c1917]">{offer.name}</h2>

      <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-serif text-3xl tabular-nums tracking-tight text-[#1c1917]">{offer.priceMain}</span>
        {offer.priceCompare ? (
          <span className="font-serif text-lg tabular-nums text-[#a89f96] line-through">{offer.priceCompare}</span>
        ) : null}
        {offer.priceSuffix ? <span className="w-full text-xs text-[#6c6258]">{offer.priceSuffix}</span> : null}
      </div>

      <p className="mt-2 text-sm text-[#5c534a]">{offer.note}</p>
      {offer.subnote ? (
        <p className="mt-1.5 text-xs leading-relaxed text-[#6c6258]">{offer.subnote}</p>
      ) : null}

      <ul className="mt-5 space-y-2">
        {offer.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2.5 text-sm text-[#4a433c]">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8b7355]" aria-hidden />
            {bullet}
          </li>
        ))}
      </ul>

      {offer.details?.length ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#8b7355] transition hover:text-[#3d3833]"
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide details' : offer.detailsLabel || 'More details'}
            <ChevronDown className={cn('h-3.5 w-3.5 transition', showDetails && 'rotate-180')} aria-hidden />
          </button>
          {showDetails ? (
            <ul className="mt-2 space-y-1.5 border-t border-[#e8e2d8]/70 pt-2">
              {offer.details.map((item) => (
                <li key={item} className="text-xs leading-relaxed text-[#6c6258]">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <footer className="mt-auto w-full shrink-0 border-t border-[#e8e2d8]/50 pt-6">
        <div className={cn('flex flex-col items-stretch justify-end', planCtaFooterMinH)}>
          {isCurrent ? (
            <div className="flex h-12 items-center justify-center">
              <span className={planStatusPillSmall} role="status">
                {offer.id === 'legacy-book' && userPlanType === 'premium' ? 'Included in your archive' : 'Current plan'}
              </span>
            </div>
          ) : cta.external ? (
            <a href={cta.href} className="block h-12 w-full shrink-0">
              <Button className={primaryCtaClass} variant={isFeaturedPrimary && !cta.fallback ? 'default' : 'outline'}>
                {showMailIcon ? (
                  <Mail
                    className={cn('mr-2 h-4 w-4 shrink-0', isFeaturedPrimary && !cta.fallback ? 'text-white' : 'text-[#8b7355]')}
                    aria-hidden
                  />
                ) : null}
                {ctaLabel}
              </Button>
            </a>
          ) : (
            <Link to={cta.href} className="block h-12 w-full shrink-0">
              <Button className={primaryCtaClass} variant={offer.featured ? 'default' : 'outline'}>
                {ctaLabel}
              </Button>
            </Link>
          )}
          <div className="mt-2 flex min-h-[2.25rem] items-start justify-center text-center">
            {ctaHelper ? (
              <p className="max-w-[17rem] text-[0.7rem] leading-snug text-[#6c6258]">{ctaHelper}</p>
            ) : null}
          </div>
        </div>
      </footer>
    </article>
  );
}

function ServiceOfferCard({ offer }) {
  const [showDetails, setShowDetails] = useState(false);
  const href = resolveServiceCta(offer);

  return (
    <article
      id={offer.anchorId}
      className={cn(
        'flex h-full flex-col rounded-2xl border border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)] p-6 shadow-sm sm:p-7',
        heritageCardHover,
        'scroll-mt-24'
      )}
    >
      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
        {offer.id === 'done-for-you' ? 'Human support' : 'Lifetime add-on'}
      </p>
      {offer.loyaltyBadge ? (
        <span className="mt-2 inline-flex w-fit rounded-full border border-[#e8e2d8]/90 bg-[#fffcf7] px-2.5 py-0.5 text-[0.62rem] font-medium tracking-[0.04em] text-[#6c6258]">
          {offer.loyaltyBadge}
        </span>
      ) : null}
      <h2 className="mt-2 font-serif text-xl font-normal text-[#1c1917] sm:text-2xl">{offer.name}</h2>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-2">
        <span className="font-serif text-2xl tabular-nums text-[#1c1917]">{offer.priceMain}</span>
        {offer.priceCompare ? (
          <span className="font-serif text-lg tabular-nums text-[#a89f96] line-through">{offer.priceCompare}</span>
        ) : null}
        {offer.priceSuffix ? <span className="w-full text-xs text-[#6c6258]">{offer.priceSuffix}</span> : null}
      </div>
      <p className="mt-2 text-sm text-[#5c534a]">{offer.note}</p>
      {offer.subnote ? (
        <p className="mt-1.5 text-xs leading-relaxed text-[#6c6258]">{offer.subnote}</p>
      ) : null}
      <ul className="mt-4 space-y-2">
        {offer.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2 text-sm text-[#4a433c]">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8b7355]" aria-hidden />
            {bullet}
          </li>
        ))}
      </ul>
      {offer.details?.length ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#8b7355] transition hover:text-[#3d3833]"
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide details' : offer.detailsLabel || 'More details'}
            <ChevronDown className={cn('h-3.5 w-3.5 transition', showDetails && 'rotate-180')} aria-hidden />
          </button>
          {showDetails ? (
            <ul className="mt-2 space-y-1.5 border-t border-[#e8e2d8]/70 pt-2">
              {offer.details.map((item) => (
                <li key={item} className="text-xs leading-relaxed text-[#6c6258]">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="mt-auto pt-6">
        <a href={href} className="block">
          <Button className={cn(planCtaPill, heritageOutlineBtn)} variant="outline">
            <Mail className="mr-2 h-4 w-4 opacity-80" aria-hidden />
            {offer.cta}
          </Button>
        </a>
        <p className="mt-2 text-center text-xs text-[#8b7355]">Available by request — no app checkout yet</p>
      </div>
    </article>
  );
}

const Pricing = () => {
  const { user } = useAuth();
  const userPlanType = user?.plan_type || null;
  const [legacyBookCheckoutEnabled, setLegacyBookCheckoutEnabled] = useState(false);
  const [billingState, setBillingState] = useState(null);

  useEffect(() => {
    trackProductEvent('pricing_viewed', { pillar: 'billing' });
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/config`);
        setLegacyBookCheckoutEnabled(Boolean(data?.legacy_book_checkout_enabled));
      } catch {
        setLegacyBookCheckoutEnabled(false);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!user) {
      setBillingState(null);
      return;
    }
    const loadBilling = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/me`);
        setBillingState(data);
      } catch {
        setBillingState(null);
      }
    };
    loadBilling();
  }, [user]);

  const showFoundingCheckoutNote =
    userPlanType === 'family' || userPlanType === 'premium' ? (
      <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-[#6c6258]">
        {userPlanType === 'family' ? (
          <>
            Already on a founding Family Archive?{' '}
            <Link to="/billing/checkout?plan=premium" className="font-medium text-[#8b7355] underline underline-offset-2">
              Expanded Legacy Archive upgrade
            </Link>{' '}
            remains available for eligible archives.
          </>
        ) : (
          <>Your founding archive includes Legacy Book export. You can also purchase Legacy Book for additional archives when checkout is available.</>
        )}
      </p>
    ) : null;

  return (
    <div className="min-h-screen heritage-surface-parchment text-[hsl(24_14%_16%)]">
        <header className="sticky top-0 z-40 border-b border-[hsl(38_16%_80%)] bg-[hsl(42_28%_96%_/_0.97)] backdrop-blur-md">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
            <Link
              to="/home"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#5c534a] transition hover:text-[#1c1917]"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              Back to home
            </Link>
            <nav className="flex items-center gap-3 text-xs font-medium text-[#68645e] sm:gap-5 sm:text-sm">
              <a href="/home#features" className="transition hover:text-[#1c1917]">
                Features
              </a>
              <Link to="/pricing" className="text-[#1c1917]">
                Plans
              </Link>
              <Link to="/faq" className="transition hover:text-[#1c1917]">
                FAQ
              </Link>
            </nav>
          </div>
        </header>

        <main>
          <section className="border-b border-[hsl(38_16%_80%)] heritage-surface-stone px-4 py-12 sm:px-6 sm:py-14">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[#8b7355]">
                People → Memories → Legacy Book
              </p>
              <h1 className="mt-3 font-serif text-3xl font-normal tracking-tight text-[#1c1917] sm:text-[2.35rem]">
                Simple pricing for a lasting family keepsake
              </h1>
              <p className="mx-auto mt-4 text-base leading-relaxed text-[#5c534a]">
                Start free, create a finished Legacy Book when you are ready, and keep building your family archive over
                time. <BrandWordmark variant="inline" as="span" /> is private stewardship — not renting memories.
              </p>
              <div className="mx-auto mt-6 flex flex-wrap justify-center gap-2 text-xs text-[#6c6258]">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e8e2d8] bg-[#fffcf7]/90 px-3 py-1">
                  <Shield className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
                  Private by default
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e8e2d8] bg-[#fffcf7]/90 px-3 py-1">
                  <BookOpen className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
                  Legacy Book at the heart
                </span>
              </div>
            </div>
          </section>

          <section className="heritage-surface-ivory px-4 py-12 sm:px-6 sm:py-14">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-serif text-2xl font-normal tracking-tight text-[hsl(24_14%_16%)] sm:text-[1.75rem]">
                Choose your path
              </h2>
              <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(32_10%_34%)]">
                Start Your Legacy is free. Legacy Book is the one-time keepsake. Family Archive subscription and memorial
                add-ons are labelled clearly when they are not checkout-ready yet.
              </p>
            </div>
            <div className="mx-auto mt-8 grid max-w-6xl gap-6 lg:grid-cols-3 lg:items-stretch lg:gap-7">
              {primaryOffers.map((offer) => (
                <PrimaryOfferCard
                  key={offer.id}
                  offer={offer}
                  userPlanType={userPlanType}
                  billingState={billingState}
                  legacyBookCheckoutEnabled={legacyBookCheckoutEnabled}
                  isLoggedIn={Boolean(user)}
                />
              ))}
            </div>
            {showFoundingCheckoutNote}
            <div className="mx-auto mt-10 max-w-6xl text-center">
              <h3 className="font-serif text-xl font-normal text-[#1c1917]">Add-ons &amp; personal help</h3>
              <p className="mx-auto mt-2 max-w-2xl text-sm text-[#6c6258]">
                Lifetime memorial pages and guided book support — available by request until checkout opens.
              </p>
            </div>
            <div className="mx-auto mt-6 grid max-w-6xl gap-6 md:grid-cols-2">
              {serviceOffers.map((offer) => (
                <ServiceOfferCard key={offer.id} offer={offer} />
              ))}
            </div>
          </section>

          <section className="border-y border-[hsl(38_16%_80%)] heritage-surface-stone px-4 py-6 sm:px-6">
            <div className="mx-auto grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {modelStrip.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 rounded-xl border border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)] px-4 py-3 shadow-sm"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[hsl(38_16%_78%)] bg-[hsl(42_28%_96%)] text-[hsl(38_36%_42%)]">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <p className="text-sm font-medium leading-snug text-[#3d3833]">{label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="heritage-surface-parchment px-4 py-10 sm:px-6 sm:py-12">
            <div className="mx-auto max-w-4xl">
              <h2 className="text-center font-serif text-xl font-normal text-[hsl(24_14%_16%)]">How it works</h2>
              <ol className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {modelSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <li
                      key={step.title}
                      className="rounded-xl border border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)] px-4 py-4 shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(38_16%_78%)] bg-[hsl(42_28%_96%)] text-[hsl(38_36%_42%)]">
                          <Icon className="h-3.5 w-3.5" aria-hidden />
                        </span>
                        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-[hsl(43_48%_58%)]">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="mt-3 font-serif text-base text-[hsl(24_14%_16%)]">{step.title}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-[hsl(32_10%_34%)]">{step.body}</p>
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>

          <section
            id="compare-plans"
            className="scroll-mt-24 border-t border-[#e8e2d8]/70 bg-[#f5f2eb]/25 px-4 py-10 sm:px-6 sm:py-12"
          >
            <div className="mx-auto max-w-6xl">
              <h2 className="text-center font-serif text-2xl font-normal text-[#1c1917]">What&apos;s included</h2>
              <p className="mx-auto mt-2 max-w-xl text-center text-sm text-[#5c534a]">
                A calm comparison across the journey. Planned and coming-soon items are labelled honestly.
              </p>
              <ComparisonGroupAccordion groups={comparisonGroups} />
            </div>
          </section>

          <section className="border-t border-[#e8e2d8]/70 px-4 py-10 sm:px-6 sm:py-12">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-center font-serif text-2xl font-normal text-[#1c1917]">Pricing questions</h2>
              <Accordion type="single" collapsible className="mt-6 rounded-xl border border-[#e8e2d8]/80 bg-[#fffcf7]/90">
                {pricingFaq.map((item, index) => (
                  <AccordionItem key={item.q} value={`faq-${index}`} className="border-[#e8e2d8]/70 px-4">
                    <AccordionTrigger className="text-left text-sm font-medium text-[#3d3833] hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-[#5c534a]">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="border-t border-[#e8e2d8]/70 px-4 py-8 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <Accordion type="single" collapsible className="rounded-xl border border-[#e8e2d8]/80 bg-[#fffcf7]/90">
                <AccordionItem value="footnotes" className="border-0 px-4">
                  <AccordionTrigger className="py-4 text-sm font-medium text-[#5c534a] hover:no-underline">
                    Important notes before purchase
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-4 border-t border-[#e8e2d8]/70 pb-4 pt-3">
                      {importantNotes.map((note) => (
                        <li key={note.id}>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8b7355]">{note.title}</p>
                          <p className="mt-1 text-sm leading-relaxed text-[#5c534a]">{note.body}</p>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          <section className="border-t border-[#e8e2d8]/70 bg-[#f5f2eb]/30 px-4 py-10 sm:px-6 sm:py-12">
            <div className="mx-auto max-w-6xl">
              <TrustProofGrid
                title="Privacy you can count on"
                subtitle="No ads, no public feed — clear controls for your family archive."
              />
            </div>
          </section>

          <section className="border-t border-[#e8e2d8]/70 px-4 py-12 sm:px-6 sm:py-14">
            <div className="mx-auto max-w-md text-center">
              <p className="text-sm leading-relaxed text-[#5c534a]">
                Start free today. Create your Legacy Book when the moment feels right.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Link to="/register" className="sm:min-w-[14rem]">
                  <Button className={cn('h-11 w-full rounded-full text-sm font-medium', heritagePrimaryBtn)}>
                    Start free
                    <ArrowRight className="ml-2 h-4 w-4 opacity-90" aria-hidden />
                  </Button>
                </Link>
                <Link to="/faq" className="sm:min-w-[9rem]">
                  <Button variant="outline" className={cn('h-11 w-full rounded-full text-sm', heritageOutlineBtn)}>
                    Read FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <PublicSiteFooter />
      </div>
  );
};

export default Pricing;
